import React from 'react';
import { X, User, Info } from 'lucide-react';
import { getErrorMessage } from '../../../shared/errors/getErrorMessage';
import { uploadClientDoc } from '../lib/clientUpload';
import {
  fetchClientDocByType, fetchClientPublicDoc, saveClientPublicDoc, saveClientDoc,
  setClientDocumentVisibility, useClientCacheReaders,
} from '../hooks/useClientDocuments';
import SmartInput from './SmartInput';
import DocUploadCard from './DocUploadCard';
import ObservacionField from './ObservacionField';
import UnidadesDeNegocioPanel from './UnidadesDeNegocioPanel';
import SubPanelOutsideIns from './SubPanelOutsideIns';
import SubPanelOperacionActiva from './SubPanelOperacionActiva';
import SubPanelFormularioNegocio from './SubPanelFormularioNegocio';
import SubPanelTratosHeader from './SubPanelTratosHeader';
import SubPanelClienteProfile from './SubPanelClienteProfile';
import SubPanelSendTerminal from './SubPanelSendTerminal';
import SubPanelCotizacion from './SubPanelCotizacion';
import SubPanelContrato from './SubPanelContrato';
import { useTratosConsolidatedData } from '../hooks/useTratosConsolidatedData';
import { isFormComplete } from '../lib/tratosForm';

// Fase 5.15 -- presentacional: `toast` llega por prop; la caché de React Query se lee vía el
// hook `useClientCacheReaders` (antes `useQueryClient()` directo).
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: siembra el array `unidades` por defecto la primera vez que el formulario carga sin datos previos.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
export const SubPanelTratos = ({ node, onClose, op, currentUserId, publicUuid, onSavedDoc, flowNodes, flowEdges, width, rightOffset, onResizeStart, toast }) => {
  const { readDocumentsByUser, readFlow } = useClientCacheReaders();
  const [formData, setFormData] = React.useState({});
  const [documents, setDocuments] = React.useState([]);
  // Id de la fila `"OperationDocument"`, necesario para el endpoint de toggle de visibilidad
  // (que identifica un documento por fila + posición en el array, no por un id propio).
  const [docRowId, setDocRowId] = React.useState(null);

  const [uploadingType, setUploadingType] = React.useState(null);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  
  const [isSaving, setIsSaving] = React.useState(false);
  const [savedFields, setSavedFields] = React.useState({});
  const [dirtyFields, setDirtyFields] = React.useState(new Set());
  const [showObs, setShowObs] = React.useState({});
  const [activeSubTab, setActiveSubTab] = React.useState('parametros');
  const [activeUnidadId, setActiveUnidadId] = React.useState(null);
  const [activeTimeTag, setActiveTimeTag] = React.useState('Mes');
  const [allDocs, setAllDocs] = React.useState([]);
  const [localFlowNodes, setLocalFlowNodes] = React.useState([]);
  const [localFlowEdges, setLocalFlowEdges] = React.useState([]);
  const [outsideViewMode, setOutsideViewMode] = React.useState('table'); // 'table' or 'json'
  const [copiedJson, setCopiedJson] = React.useState(false);
  
  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;
  const documentsRef = React.useRef(documents);
  documentsRef.current = documents;
  const saveTimeout = React.useRef(null);

  // Este `useState` vivía
  // después del `if (!node) return null;` de abajo — si `node` pasara de falsy a truthy entre
  // renders de una misma instancia montada, React llamaría una cantidad distinta de hooks
  // (violación de las Rules of Hooks). Se mueve antes del return condicional; el `node?.` cubre
  // el caso en que este render en particular no tiene `node` (el componente va a retornar null
  // de todas formas, el valor inicial de este estado no se usa en ese caso).
  const [resolvedNodeSub, setResolvedNodeSub] = React.useState(() => {
    return (node?.sub || node?.label || '').toLowerCase().trim();
  });

  const nodesSource = (flowNodes && flowNodes.length > 0) ? flowNodes : localFlowNodes;
  const edgesSource = (flowEdges && flowEdges.length > 0) ? flowEdges : localFlowEdges;

  // 04-enforce.md 4.1 -- el BFS de nodos upstream + las 4 vistas consolidadas del tab "Outside
  // ins" (antes ~155 líneas de memos acá) viven ahora en `useTratosConsolidatedData`. El
  // container sólo le pasa datos ya cargados. Se llama antes del `return null` de abajo para no
  // romper el orden de hooks (mismo motivo que el `useState` de `resolvedNodeSub`); el hook
  // maneja `node: null` devolviendo vistas vacías.
  const { currentItems, receivedItems, availableVariables, consolidatedJson } = useTratosConsolidatedData({
    node, nodesSource, edgesSource, allDocs, formData, documents, resolvedNodeSub,
  });

  React.useEffect(() => {
    const fetchExistingData = async () => {
      if (publicUuid) {
        try {
             const json = await fetchClientPublicDoc(publicUuid);
             if (json.doc) {
               let loadedData = json.doc.form_data || {};
               if ((json.doc.document_type || '').toLowerCase().includes('form') && (!loadedData.unidades || loadedData.unidades.length === 0)) {
                 const newId = Math.random().toString(36).substring(2, 9);
                 loadedData.unidades = [{
                   id: newId, nombre: 'Unidad 1', direccion: loadedData.direccion || '',
                   terminales_num: loadedData.terminales_num || '', tx_mes: loadedData.tx_mes || '',
                   ventas_anuales: loadedData.ventas_anuales || '', ticket_promedio: loadedData.ticket_promedio || '',
                   hora_apertura: loadedData.hora_apertura || '', hora_salida: loadedData.hora_salida || '',
                   mix_debito: loadedData.mix_debito || 30, mix_credito: loadedData.mix_credito || 50, mix_intl: loadedData.mix_intl || 20
                 }];
                 setActiveUnidadId(newId);
               } else if (loadedData.unidades && loadedData.unidades.length > 0) {
                 setActiveUnidadId(loadedData.unidades[0].id);
               }
               setFormData(loadedData);
               setDocuments(json.doc.documents || []);
               setDocRowId(json.doc.id ?? null);
               if (json.doc.document_type) {
                 setResolvedNodeSub(json.doc.document_type);
               }
               // Also fetch all other docs using the user_id from this doc -- vía cache
               // compartida con `OpDetailPanel.jsx` (mismo `uid`, mismo query key).
               const uid = json.doc.user_id;
               if (uid) {
                 const docs = await readDocumentsByUser(uid);
                 setAllDocs(docs);
                 // Load flow for the deal as well
                 const flow = await readFlow(uid);
                 if (flow.nodes) setLocalFlowNodes(flow.nodes);
                 if (flow.edges) setLocalFlowEdges(flow.edges);
               }
             }
        } catch (e) { console.error("Error loading public config:", e); toast.error(getErrorMessage(e)); }
        return;
      }

      // `uid` es en realidad el id del trato (`ClientOperation.id`, un UUID) -- el fallback a
      // `currentUserId` (un id de usuario, entero) nunca produce un uid válido contra la FK real
      // de "OperationDocument". `OpDetailPanel` (único padre de este componente) siempre trae un
      // `op` real, así que este fallback no debería dispararse en la práctica.
      const uid = op && op.id;
      if (!uid) return;

      // Load current document
      if (resolvedNodeSub) {
        try {
          const json = await fetchClientDocByType(uid, resolvedNodeSub);
          if (json.doc) {
            let loadedData = json.doc.form_data || {};
            if (resolvedNodeSub.includes('form') && (!loadedData.unidades || loadedData.unidades.length === 0)) {
              const newId = Math.random().toString(36).substring(2, 9);
              loadedData.unidades = [{
                id: newId, nombre: 'Unidad 1', direccion: loadedData.direccion || '',
                terminales_num: loadedData.terminales_num || '', tx_mes: loadedData.tx_mes || '',
                ventas_anuales: loadedData.ventas_anuales || '', ticket_promedio: loadedData.ticket_promedio || '',
                hora_apertura: loadedData.hora_apertura || '', hora_salida: loadedData.hora_salida || '',
                mix_debito: loadedData.mix_debito || 30, mix_credito: loadedData.mix_credito || 50, mix_intl: loadedData.mix_intl || 20
              }];
              setActiveUnidadId(newId);
            } else if (loadedData.unidades && loadedData.unidades.length > 0) {
              setActiveUnidadId(loadedData.unidades[0].id);
            }
            setFormData(loadedData);
            setDocuments(json.doc.documents || []);
            setDocRowId(json.doc.id ?? null);
          }
        } catch (e) { console.error("Error loading existing config:", e); toast.error(getErrorMessage(e)); }
      }

      // Load all documents for the client -- vía cache compartida con `OpDetailPanel.jsx`.
      try {
        const docs = await readDocumentsByUser(uid);
        setAllDocs(docs);
      } catch (e) { console.error("Error loading all documents:", e); toast.error(getErrorMessage(e)); }

      // Load flow nodes and edges for upstream calculations -- vía cache compartida.
      try {
        const flow = await readFlow(uid);
        if (flow.nodes) setLocalFlowNodes(flow.nodes);
        if (flow.edges) setLocalFlowEdges(flow.edges);
      } catch (e) { console.error("Error loading client flow:", e); toast.error(getErrorMessage(e)); }
    };
    fetchExistingData();
  }, [currentUserId, op, resolvedNodeSub, publicUuid, readDocumentsByUser, readFlow, toast]);

  React.useEffect(() => {
    if (resolvedNodeSub && resolvedNodeSub.includes('form')) {
      if (!formData.unidades || formData.unidades.length === 0) {
        setFormData(prev => {
          if (prev.unidades && prev.unidades.length > 0) return prev;
          const newId = Math.random().toString(36).substring(2, 9);
          setActiveUnidadId(newId);
          return {
            ...prev,
            unidades: [{
              id: newId, nombre: 'Unidad 1', direccion: prev.direccion || '',
              terminales_num: prev.terminales_num || '', tx_mes: prev.tx_mes || '',
              ventas_anuales: prev.ventas_anuales || '', ticket_promedio: prev.ticket_promedio || '',
              hora_apertura: prev.hora_apertura || '', hora_salida: prev.hora_salida || '',
              mix_debito: prev.mix_debito || 30, mix_credito: prev.mix_credito || 50, mix_intl: prev.mix_intl || 20
            }]
          };
        });
      }
    }
  }, [resolvedNodeSub, formData]);

  // Fase 5.1 — todos los hooks (incluidos estos 2 `useEffect`) van antes del `return null`:
  // antes estaban después y `rules-of-hooks` (en `warn`) lo dejó pasar; con `node` alternando a
  // null el conteo de hooks cambiaba entre renders.
  if (!node) return null;
  const isCotiza = resolvedNodeSub.includes('cotiza');
  const isContrato = resolvedNodeSub.includes('contrato');
  const isClienteNode = node.category === 'cliente' || node.category === 'clientes' || resolvedNodeSub === 'cliente' || resolvedNodeSub === 'clientes' || resolvedNodeSub.includes('cliente');

  const handleCopyJson = () => {
    const jsonStr = JSON.stringify(consolidatedJson, null, 2);
    navigator.clipboard.writeText(jsonStr);
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 2000);
  };

  const formComplete = isFormComplete(formData, documents);

  // Decisión explícita (02.3-client.md §4, paso a): `saveTimeout` no se cancela al desmontar
  // -- comportamiento preexistente, preservado tal cual. El timer sigue vivo tras el desmonte y
  // dispara `saveToDB()` igual (los refs `formDataRef`/`documentsRef` sobreviven por closure),
  // lo que genera un warning real de "setState en componente desmontado" pero NO pierde datos
  // salvo que además falle la red justo en ese momento (error ya sin toast posible, solo
  // `console.error`). No se agrega un cleanup que cancele el timer en este paso -- cambiar esto
  // es una decisión de comportamiento aparte, no un efecto secundario de mover el fetch a un
  // hook.
  const saveToDB = async () => {
    setIsSaving(true);
    try {
      const json = publicUuid
        ? await saveClientPublicDoc(publicUuid, {
            documents: documentsRef.current,
            form_data: formDataRef.current,
          })
        : await saveClientDoc({
            user_id: (op && op.id) || null,
            client_type: (op && op.category) || 'default',
            document_type: resolvedNodeSub,
            documents: documentsRef.current,
            form_data: formDataRef.current,
          });
      if (json.doc) {
          // El backend resuelve `documents[].path` a una URL usable antes de responder —
          // sin esto, un documento recién subido queda sin `url` hasta el próximo fetch.
          setDocuments(json.doc.documents || []);
          setDocRowId(json.doc.id ?? null);
          if (onSavedDoc) onSavedDoc(json.doc);
      }

      setDirtyFields(prev => {
        const recentlySaved = Array.from(prev);
        recentlySaved.forEach(f => {
            setSavedFields(s => ({...s, [f]: true}));
            setTimeout(() => setSavedFields(s => ({...s, [f]: false})), 2000);
        });
        return new Set();
      });
    } catch (e) {
      console.error("Auto-save error:", e);
      toast.error(getErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (publicUuid && !formData.is_public) return;
    setFormData(prev => ({ ...prev, [field]: value }));
    setDirtyFields(prev => new Set([...prev, field]));
    
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(saveToDB, 1000);
  };

  const handleUnidadChange = (unidadId, field, value) => {
    if (publicUuid && !formData.is_public) return;
    setFormData(prev => {
      const uArr = prev.unidades ? [...prev.unidades] : [];
      const idx = uArr.findIndex(u => u.id === unidadId);
      if (idx !== -1) {
        uArr[idx] = { ...uArr[idx], [field]: value };
      }
      return { ...prev, unidades: uArr };
    });
    setDirtyFields(prev => new Set([...prev, 'unidades']));
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(saveToDB, 1000);
  };

  const handleFileUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingType(docType);
    setUploadProgress(0);

    try {
        // `client_avatar` (para `cliente_img`) es público; `client_document` es privado por
        // defecto, con toggle propio. Mapeo en `lib/clientUpload.js`.
        const response = await uploadClientDoc(file, docType, setUploadProgress);

        if (docType === 'cliente_img') {
            // Público — la URL es estable, se persiste directo (nunca expira).
            handleInputChange('cliente_img', response.url);
        } else {
            setDocuments(prev => {
                const filtered = prev.filter(d => d.type !== docType);
                return [...filtered, {
                    type: docType,
                    path: response.path,
                    mimeType: response.type,
                    access: 'private',
                    name: file.name,
                }];
            });
        }
        clearTimeout(saveTimeout.current);
        saveTimeout.current = setTimeout(saveToDB, 500);
    } catch (err) {
        console.error('Document upload error', err);
    } finally {
        setUploadingType(null);
    }
  };

  // 02.3-client.md paso b -- la presentación se movió a `ObservacionField.jsx`; este wrapper
  // preserva la firma `renderObservaciones(field)` que ya usan `renderAutoInput`/
  // `renderAutoTextarea`/`renderUploadBtn` en muchos call sites, sin tener que tocar cada uno.
  const renderObservaciones = (field) => {
     const obsKey = `obs_${field}`;
     const obsOkKey = `obs_ok_${field}`;
     return (
        <ObservacionField
           field={field}
           text={formData[obsKey]}
           isOk={!!formData[obsOkKey]}
           isPublic={!!publicUuid}
           isExpanded={!!showObs[field]}
           onToggleExpand={() => setShowObs(p => ({...p, [field]: !p[field]}))}
           onToggleOk={() => handleInputChange(obsOkKey, !formData[obsOkKey])}
           onChangeText={(val) => handleInputChange(obsKey, val)}
        />
     );
  };

  const renderAutoInput = (field, type='text', props={}, icon=null) => {
    const saved = savedFields[field];
    return (
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <SmartInput
          type={type}
          name={field}
          value={formData[field] || ''}
          onChange={val => handleInputChange(field, val)}
          placeholder={props.placeholder}
          availableVariables={publicUuid ? [] : availableVariables}
          disabled={publicUuid && !formData.is_public}
          icon={icon}
          style={{ borderColor: saved ? '#10b981' : '#333', transition: 'border-color 0.3s', ...(props.style || {}) }}
          {...props}
        />
        {!isCotiza && !isContrato && renderObservaciones(field)}
      </div>
    );
  };

  const renderAutoTextarea = (field, isMachote, defaultValue, props={}, icon=null) => {
    const saved = savedFields[field];
    return (
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <SmartInput
          type="textarea"
          name={field}
          value={formData[field] ?? defaultValue ?? ''}
          onChange={val => handleInputChange(field, val)}
          placeholder={props.placeholder}
          availableVariables={publicUuid ? [] : availableVariables}
          disabled={publicUuid && !formData.is_public}
          icon={icon}
          className={isMachote ? "op-sub-panel-machote" : ""}
          style={{ 
            borderColor: saved ? '#10b981' : '#333', 
            transition: 'border-color 0.3s',
            ...(props.style || {})
          }}
          {...props}
        />
        {!isCotiza && !isContrato && renderObservaciones(field)}
      </div>
    );
  };

  // Toggle de
  // visibilidad de un documento puntual — identificado por su posición en `documents[]`, no
  // por un id propio (el array no lo tiene). Sin restricción de autorización (decisión
  // tomada a propósito) — el endpoint responde igual con o sin sesión.
  const handleToggleDocVisibility = async (type) => {
    const index = documents.findIndex(d => d.type === type);
    if (index === -1 || !docRowId) return;
    const doc = documents[index];
    const newAccess = doc.access === 'private' ? 'public' : 'private';
    try {
      const json = await setClientDocumentVisibility(docRowId, index, newAccess);
      if (!json.success) return;
      setDocuments(prev => prev.map((d, i) => i === index ? { ...d, access: newAccess, url: json.url } : d));
    } catch (e) {
      console.error('Toggle visibility error', e);
      toast.error(getErrorMessage(e));
    }
  };

  // 02.3-client.md paso b -- la tarjeta en sí se movió a `DocUploadCard.jsx`; este wrapper
  // preserva la firma `renderUploadBtn(label, type)` que ya usan 14 call sites (incluyendo
  // `UnidadesDeNegocioPanel` vía la render-prop `renderUpload`), sin tener que tocar cada uno.
  const renderUploadBtn = (label, type) => {
    const doc = documents.find(d => d.type === type);
    const isUploadingThis = uploadingType === type;
    const isReadOnly = publicUuid && !formData.is_public;
    return (
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <DocUploadCard
          label={label}
          doc={doc}
          isUploading={isUploadingThis}
          progress={isUploadingThis ? uploadProgress : 0}
          isReadOnly={isReadOnly}
          inputId={'file-' + type}
          onOpenPicker={() => document.getElementById('file-' + type).click()}
          onFileChange={e => handleFileUpload(e, type)}
          onToggleVisibility={doc?.path ? () => handleToggleDocVisibility(type) : undefined}
          onPreview={() => window.open(doc?.url, '_blank')}
        />
        {renderObservaciones(type)}
      </div>
    );
  };

  const isContacto1Complete = !!(formData.contacto_1_nombre && formData.contacto_1_correo && formData.contacto_1_tel);
  const isContacto2Complete = !!(formData.contacto_2_nombre && formData.contacto_2_correo && formData.contacto_2_tel);
  const isContacto3Complete = !!(formData.contacto_3_nombre && formData.contacto_3_correo && formData.contacto_3_tel);

  const handleAddUnidad = () => {
    const newId = Math.random().toString(36).substring(2, 9);
    const uArr = formData.unidades ? [...formData.unidades] : [];
    uArr.push({
      id: newId, nombre: `Unidad ${uArr.length + 1}`, direccion: '',
      terminales_num: '', tx_mes: '', ventas_anuales: '', ticket_promedio: '',
      hora_apertura: '', hora_salida: '', mix_debito: 30, mix_credito: 50, mix_intl: 20
    });
    handleInputChange('unidades', uArr);
    setActiveUnidadId(newId);
  };

  // 02.3-client.md paso b -- la presentación se movió a `UnidadesDeNegocioPanel.jsx`; se le
  // pasa `renderUploadBtn` como render-prop para que reutilice la misma composición
  // DocUploadCard+ObservacionField que el resto del container, en vez de duplicarla.
  const renderUnidadesDeNegocioTabs = () => (
    <UnidadesDeNegocioPanel
      unidades={formData.unidades || []}
      activeUnidadId={activeUnidadId}
      onSelectUnidad={setActiveUnidadId}
      onAddUnidad={handleAddUnidad}
      onUnidadFieldChange={handleUnidadChange}
      renderUpload={renderUploadBtn}
    />
  );

  return (
    <div className="op-sub-panel-tratos" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} style={{ width: width, right: rightOffset }}>
      <SubPanelTratosHeader
        isCotiza={isCotiza}
        isContrato={isContrato}
        isClienteNode={isClienteNode}
        resolvedNodeSub={resolvedNodeSub}
        formData={formData}
        op={op}
        publicUuid={publicUuid}
        isSaving={isSaving}
        onClose={onClose}
        onResizeStart={onResizeStart}
        activeSubTab={activeSubTab}
        onChangeSubTab={setActiveSubTab}
        onChangeField={handleInputChange}
      />
      <div className="op-sub-panel-body">
        {activeSubTab === 'parametros' ? (
          <>
            {isClienteNode ? (
              <SubPanelClienteProfile
                formData={formData}
                op={op}
                publicUuid={publicUuid}
                onFileUpload={handleFileUpload}
                renderAutoInput={renderAutoInput}
                renderUploadBtn={renderUploadBtn}
              />
            ) : resolvedNodeSub === 'operacion-activa' ? (
              <SubPanelOperacionActiva op={op} activeTimeTag={activeTimeTag} onChangeTimeTag={setActiveTimeTag} />
            ) : (
              <>
                <div className="op-sub-panel-field">
                  <label>Correo</label>
                  {renderAutoInput("correo", "email", { placeholder: "correo@ejemplo.com" }, <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>)}
                </div>
                <div className="op-sub-panel-field">
                  <label>Número de teléfono</label>
                  {renderAutoInput("telefono", "tel", { placeholder: "+56 9 1234 5678" }, <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>)}
                </div>
              </>
            )}

        {resolvedNodeSub.includes('send-terminal') && (
          <SubPanelSendTerminal
            formData={formData}
            publicUuid={publicUuid}
            availableVariables={availableVariables}
            savedFields={savedFields}
            uploadingType={uploadingType}
            uploadProgress={uploadProgress}
            documents={documents}
            isCotiza={isCotiza}
            isContrato={isContrato}
            onChangeField={handleInputChange}
            onUploadGuidePhoto={handleFileUpload}
            onDeleteGuidePhoto={() => {
              setDocuments(prev => prev.filter(d => d.type !== 'foto_guia'));
              clearTimeout(saveTimeout.current);
              saveTimeout.current = setTimeout(saveToDB, 500);
            }}
            renderAutoTextarea={renderAutoTextarea}
            renderObservaciones={renderObservaciones}
          />
        )}

        {resolvedNodeSub.includes('mou') && (
          <div className="op-sub-panel-field">
            {renderAutoTextarea("mou_text", true, `MEMORÁNDUM DE ENTENDIMIENTO (MoU)\n\nQUE CELEBRAN POR UNA PARTE\n[Nombre de la Empresa A], representada por [Nombre del representante], a quien en lo sucesivo se le denominará “LA PARTE A”,\n\nY POR OTRA PARTE\n[Nombre de la Empresa B], representada por [Nombre del representante], a quien en lo sucesivo se le denominará “LA PARTE B”,\n\nAL TENOR DE LOS SIGUIENTES ANTECEDENTES Y CLÁUSULAS:\n\nI. ANTECEDENTES\nLA PARTE A se dedica a [descripción breve del negocio, ej. desarrollo de software, infraestructura de pagos, etc.].\nLA PARTE B se dedica a [ej. distribución de terminales, hardware, soluciones de cobro, etc.].\nAmbas partes tienen interés en colaborar para la implementación, distribución y/o operación de terminales de pago / dispositivos inteligentes.\nII. OBJETO\n\nEl presente Memorándum tiene como objetivo establecer las bases de colaboración entre las partes para:\n\nLa integración tecnológica de terminales.\nLa comercialización y/o distribución de dichas terminales.\nLa operación de servicios de pago a través de las mismas.`)}
          </div>
        )}

        {isCotiza && <SubPanelCotizacion renderAutoInput={renderAutoInput} />}

        {isContrato && (
          <SubPanelContrato
            value={formData['contrato_text']}
            onChange={val => handleInputChange('contrato_text', val)}
          />
        )}

        {resolvedNodeSub.includes('form') && (
          <SubPanelFormularioNegocio
            formData={formData}
            savedFields={savedFields}
            publicUuid={publicUuid}
            isFormComplete={formComplete}
            isContacto1Complete={isContacto1Complete}
            isContacto2Complete={isContacto2Complete}
            isContacto3Complete={isContacto3Complete}
            onChangeField={handleInputChange}
            renderAutoInput={renderAutoInput}
            renderAutoTextarea={renderAutoTextarea}
            renderUploadBtn={renderUploadBtn}
            renderUnidadesDeNegocioTabs={renderUnidadesDeNegocioTabs}
          />
        )}


          </>
        ) : activeSubTab === 'outside-ins' ? (
          <SubPanelOutsideIns
            viewMode={outsideViewMode}
            onChangeViewMode={setOutsideViewMode}
            currentItems={currentItems}
            receivedItems={receivedItems}
            consolidatedJson={consolidatedJson}
            copiedJson={copiedJson}
            onCopyJson={handleCopyJson}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '300px', color: 'rgba(255,255,255,0.3)', fontSize: '13px', fontStyle: 'italic', padding: 'var(--space-12) var(--space-5)', textAlign: 'center' }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: 'var(--space-3)', opacity: 0.5, color: '#f97316' }}><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
            <div>Sección sin configurar por ahora</div>
          </div>
        )}
      </div>
    </div>
  );
};
